export const UNCATEGORIZED = "Uncategorized";

export type CategoryGroup<T> = { category: string; rows: T[] };

function compare(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base", numeric: true });
}

/**
 * Groups rows by category, sorting categories A–Z and rows A–Z by name
 * within each one. Rows with no category go in a final "Uncategorized" group.
 *
 * A non-empty `query` keeps rows whose name or category contains it
 * (case-insensitive), and groups left empty by the filter are dropped.
 */
export function groupByCategory<T extends { name: string; category: string | null }>(
  rows: T[],
  query = "",
): CategoryGroup<T>[] {
  const q = query.trim().toLowerCase();
  // Keyed case-insensitively so "Sauces" and "sauces " share one group,
  // labelled with the first spelling seen.
  const groups = new Map<string, CategoryGroup<T>>();

  for (const row of rows) {
    const category = row.category?.trim().replace(/\s+/g, " ") || UNCATEGORIZED;
    if (q && !row.name.toLowerCase().includes(q) && !category.toLowerCase().includes(q)) {
      continue;
    }
    const key = category.toLowerCase();
    const group = groups.get(key);
    if (group) group.rows.push(row);
    else groups.set(key, { category, rows: [row] });
  }

  return [...groups.values()]
    .sort((a, b) => {
      if (a.category === UNCATEGORIZED) return 1;
      if (b.category === UNCATEGORIZED) return -1;
      return compare(a.category, b.category);
    })
    .map((group) => ({
      category: group.category,
      rows: group.rows.sort((a, b) => compare(a.name, b.name)),
    }));
}
